import * as React from "react";
import moment from 'moment';
import { Grid, GridToolbar, GridColumn, GridCellProps, GridSortChangeEvent, GridEditCell,  } from '@progress/kendo-react-grid';
import { Button } from "@progress/kendo-react-buttons";
import { orderBy, SortDescriptor, filterBy, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Switch, Input, Checkbox, CheckboxChangeEvent, NumericTextBox } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
    IStateLoading, IConfigurationData, AuthorizedProps, ILookupItem, IDropdownData,
    IColumn, IFileDestination, IDataLoaderConfig, IConfigurationEntity, IValidationData
} from "../Model";
import { Server } from "../../server/Server";
import DropDownCell from "../Common/DropDownCell";
import { apiConfig } from "../../msal/Config";
import WaitLoading from "../Common/WaitLoading";
import "./ConfigEditor.scss";
import { AppAlert } from '../../server/AppAlert';
import { exportIcon, copyIcon, plusIcon, trashIcon, saveIcon, cancelIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';



const apiUrls = {
    search: "api/ConfigEditor/search",
    lookups: "api/ConfigEditor/lookups",
    crud: "api/ConfigEditor",
    export: "api/ConfigEditor/export",
}

interface IProp extends AuthorizedProps {
}

interface IState extends IStateLoading {
    configData: IConfigurationData[];
    viewData: IConfigurationData[];
    sort: SortDescriptor[];
    filter: CompositeFilterDescriptor;
    lookups: any;
    selectedIndex: number;
    configItem?: IConfigurationData;
    //pKey?: string;
    //rKey?: string;
    edited: boolean;
    confirmVisible: boolean;
    confirmAction: any;
    confirmTitle?: string;
    dataSwitch: boolean;
    validationData: IValidationData;
    skip: number;
}

export default class ConfigEditor extends React.Component<IProp, IState> {

    private grid: Grid | null = null;
    private tokenRetryTimer?: number;
    private readonly maxTokenRetries = 20;

    constructor(props: any) {
        super(props);
        this.state = {
            skip: 0,
            loading: false,
            configData: [],
            viewData: [],
            sort: [],
            filter: {
                logic: "and",
                filters: []
            },
            lookups: {},
            edited: false,
            confirmVisible: false,
            confirmAction: null,
            dataSwitch: false,
            selectedIndex: -1,
            validationData: {} as IValidationData,
        }
    }

    public async componentDidMount() {
        this.setState({
            loading: true
        });
        this.loadInitialData();
    }

    public componentWillUnmount() {
        if (this.tokenRetryTimer) {
            window.clearTimeout(this.tokenRetryTimer);
            this.tokenRetryTimer = undefined;
        }
    }

    private loadInitialData = async (attempt: number = 0) => {
        const apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        if (!apiAccessToken) {
            if (attempt < this.maxTokenRetries) {
                this.tokenRetryTimer = window.setTimeout(() => this.loadInitialData(attempt + 1), 800);
                return;
            }

            this.setState({
                loading: false
            });
            return;
        }

        if (this.tokenRetryTimer) {
            window.clearTimeout(this.tokenRetryTimer);
            this.tokenRetryTimer = undefined;
        }

        await Promise.all([
            this.getLookups(apiAccessToken),
            this.getConfigData(apiAccessToken)
        ]);

        this.setState({
            loading: false
        });
    }

    public render() {
        //if (this.state.loading)
        //    return (<WaitLoading />);

        return (
            <div className="config-editor">
                {this.state.loading && <WaitLoading />}
                <div className="config-editor-list">
                    <Grid
                        data={this.state.viewData.map((item, index) => ({
                            ...item,
                            selected: index === this.state.selectedIndex
                        })).slice(this.state.skip, this.state.skip + 20)}
                        className="config-data-grid"
                        resizable={true}
                        filterable={true}
                        filter={this.state.filter}
                        onFilterChange={this.onFilterChange}
                        sortable={true}
                        sort={this.state.sort}
                        onSortChange={this.sortGrid}
                        onRowClick={this.onRowClick}
                        selectedField="selected"
                        dataItemKey="fileConfig.fileName"
                        selectable={{
                            enabled: true,
                            mode: 'single'
                        }}
                        ref={g => this.grid = g}
                        rowHeight={40}
                        pageSize={20}
                        onPageChange={this.pageChange}
                        scrollable="virtual"
                        skip={this.state.skip}
                        total={this.state.viewData.length}
                    >
                        <GridToolbar>
                            <div className="section-header">
                                <h6>GDL Configuration Data</h6>
                                <div className="toolbar-right">
                                    <Button
                                        title="Export"
                                        onClick={this.exportConfig}
                                        svgIcon={exportIcon}
                                        className="cmd export"
                                        type="button"> Export</Button>&nbsp;
                                    <Button
                                        title="Add new item"
                                        onClick={this.checkAddItem}
                                        svgIcon={plusIcon}
                                        className="cmd add-item"
                                        disabled={this.state.configItem?.isNew}
                                        type="button"> Add item</Button>
                                </div>
                            </div>
                        </GridToolbar>
                        <GridColumn field="fileConfig.fileName" title="File name" width="300px" resizable sortable />
                        <GridColumn field="fileConfig.fileType" title="File type" width="80px" resizable sortable />
                        <GridColumn field="fileConfig.source.sourceKey" title="Source key" width="100px" resizable sortable />
                        <GridColumn field="fileConfig.downloadType" title="Source type" width="115px" resizable sortable />
                        <GridColumn field="fileConfig.source.sourceName" title="Source Name" width="120px" resizable sortable />
                        <GridColumn field="fileConfig.source.sourceFolder" title="Source folder" width="115px" resizable sortable />
                        <GridColumn field="fileConfig.source.removeFile" title="Remove file" width="105px" resizable filterable={
                            false} sortable cells={{ data: this.booleanCell }} />
                        <GridColumn field="muleDisabled" title="Mule Disabled" width="105px" resizable filter="boolean" sortable cells={{ data: this.booleanCell }} />
                        <GridColumn field="dataConfig.dbName" title="DB name" width="120px" sortable />
                        <GridColumn field="dataConfig.tableName" width="300px" title="Table name" sortable />
                        <GridColumn field="dataConfig.columnlist" width="150px" title="DB Columns" cells={{ data: this.columnsCell() }} />
                        <GridColumn field="dataConfig.archive" title="Archive folder" width="125px" resizable />
                        <GridColumn field="dataConfig.colSep" title="Col Sep" width="75px" resizable />
                        <GridColumn field="dataConfig.truncateTable" title="Truncate table" width="120px" resizable filterable={
                            false} sortable cells={{ data: this.booleanCell }} />
                        <GridColumn field="timestamp" title="Timestamp" width="110px" resizable sortable cells={{ data: this.dateCell() }} />
                        <GridColumn field="user" title="User" width="180px" resizable sortable />
                    </Grid>
                </div >
                {this.state.configItem &&
                    <form autoComplete="off">
                        <div className="config-editor-toolbar">
                            <div className="toolbar-left">
                                {this.state.edited &&
                                    <Button
                                        title="Apply changes"
                                        onClick={this.checkApplyChanges}
                                        svgIcon={ saveIcon}
                                        className="cmd apply-changes"
                                        type="button"
                                    > Apply changes</Button>
                                }
                                {this.state.edited &&
                                    <Button
                                        title="Cancel changes"
                                        onClick={this.checkCancelChanges}
                                        svgIcon={cancelIcon}
                                        icon="cancel"
                                        className="cmd cancel-changes"
                                        type="button"
                                    > Cancel changes</Button>
                                }
                            </div>
                            <div className="toolbar-right">
                                <Button
                                    title="Copy and paste item as a new item"
                                    onClick={this.duplicateItem}
                                    svgIcon={copyIcon}
                                    icon="copy"
                                    className="cmd duplicate-item"
                                    disabled={this.state.edited}
                                    type="button">Duplicate item</Button>
                                <Button
                                    title="Delete item"
                                    onClick={this.checkDeleteItem}
                                    svgIcon={ trashIcon }
                                    icon="delete"
                                    className="cmd delete-item"
                                    disabled={this.state.edited}
                                    type="button"> Delete item</Button>
                            </div>
                        </div>
                        <div className="config-editor-errors">
                            {this.listErrors()}
                            {this.listWarnings()}
                        </div>
                        <div className="config-editor-details">
                            <div className="config-editor-file">
                                <div className="section-header">
                                    <h6>
                                        File extractor configuration
                                    </h6>
                                </div>
                                <div className="section-body" >
                                    <div className="row" style={{ display: "flex" }}>
                                        <div className="col-4 info-label">
                                            <label htmlFor="status">GDL Status:</label>
                                        </div>
                                        <div className="col-8 data-input">
                                            <Switch onLabel="Enabled" offLabel="Disabled" id="gdlStatus" className="config-status custom-switch-wrapper" name="gdlStatus"
                                                checked={!this.state.configItem.disabled} onChange={this.toggleDisabled} />
                                        </div>
                                    </div>
                                    <div className="row" style={{ display: "flex" }}>
                                        <div className="col-4 info-label">
                                            <label htmlFor="status">Mule Status:</label>
                                        </div>
                                        <div className="col-8 data-input">
                                            <Switch onLabel="Enabled" offLabel="Disabled" id="muleStatus" className="config-status" name="muleStatus"
                                                checked={!this.state.configItem.muleDisabled} onChange={this.toggleDisabled} />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="filename">File name:</label>
                                        </div>
                                        <div className="col-8 data-input">
                                            <Input id="filename" name="fileConfig.fileName"
                                                value={this.state.configItem.fileConfig.fileName}
                                                required={true}
                                                onChange={this.changeTextValue}
                                                pattern="^\S*$" title="File name pattern" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="filetype">File type:</label>
                                        </div>
                                        <div className="col-4 data-input">
                                            <DropDownList
                                                onChange={this.changeLookupItem}
                                                name="fileConfig.fileType"
                                                textField="name"
                                                value={this.getDropdownValue(this.state.configItem.fileConfig.fileType)}
                                                data={this.getLookupDropdownValues("FileType")}
                                                defaultItem=""
                                                className="drop-down" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="regex">Regex:</label>
                                        </div>
                                        <div className="col-8 data-input">
                                            <Input
                                                key={`${this.state.configItem.partitionKey}-${this.state.configItem.rowKey}-regex`}
                                                id="regex" name="fileConfig.source.regex"
                                                value={this.state.configItem.fileConfig.source.regex ?? ""}
                                                required={true}
                                                onChange={this.changeTextValue}
                                                pattern="^\S*$" title="Source Name" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="sourceKey">Source key:</label>
                                        </div>
                                        <div className="col-4 data-input">
                                            <DropDownList
                                                onChange={this.changeSourceKey}
                                                name="fileConfig.source.sourceKey"
                                                textField="name"
                                                value={this.getDropdownValue(this.state.configItem.fileConfig.source
                                                    .sourceKey)}
                                                data={this.getLookupDropdownValues("SourceKey")}
                                                defaultItem=""
                                                className="drop-down" />
                                        </div>
                                        <div className="col-4">
                                            <span className="info-label">Source type:</span>{this.state.configItem
                                                .fileConfig.downloadType}
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="sourcename">Source name:</label>
                                        </div>
                                        <div className="col-8 data-input">
                                            <Input id="sourcename" name="fileConfig.source.sourceName"
                                                value={this.state.configItem.fileConfig.source.sourceName}
                                                required={true}
                                                onChange={this.changeTextValue}
                                                pattern="^\S*$" title="Source Name" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="sourcefolder">Source folder:</label>
                                        </div>
                                        <div className="col-8 data-input">
                                            <Input id="sourcefolder" name="fileConfig.source.sourceFolder"
                                                value={this.state.configItem.fileConfig.source.sourceFolder}
                                                required={false}
                                                onChange={this.changeTextValue}
                                                pattern="^\S*$" title="Source Folder" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="sourceArchive">FTP archive:</label>
                                        </div>
                                        <div className="col-8 data-input">
                                            <Input id="sourceArchive" name="fileConfig.source.sourceArchive"
                                                value={this.state.configItem.fileConfig.source.sourceArchive}
                                                required={false}
                                                onChange={this.changeTextValue}
                                                pattern="^\S*$" title="FTP archive folder" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="removeFile" title="Remove file from source after extraction">Remove file:</label>
                                        </div>
                                        <div className="col-4 data-input">
                                            <Checkbox
                                                id="removeFile"
                                                name="fileConfig.source.removeFile"
                                                value={this.state.configItem.fileConfig.source.removeFile}
                                                onChange={this.changeBooleanValue}
                                                label="" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-4 info-label">
                                            <label htmlFor="frequency" title="Frequency">Frequency:</label>
                                        </div>
                                        <div className="col-4 data-input">
                                            <Input id="frequency" name="fileConfig.frequency"
                                                value={this.state.configItem.fileConfig.frequency}
                                                required={false}
                                                onChange={this.changeTextValue}
                                                title="Frequency in minutes(multiple of 5) or use Day/Half or specify times in HH:mm format"
                                                pattern="^5|[1-9][0|5]|[1-9][0-9][0|5]|Day|Half|((([0-1][0-9]|[0-9]|[2][0-3]):([0-5][0-9]|[0-9])(,|;)?)+)$" />
                                        </div>
                                    </div>
                                    <div>
                                        <Grid data={this.state.configItem.fileConfig.destinations.filter(c => !c.isRemoved)
                                        }
                                            className="file-config-destinations-grid"
                                            resizable={true}
                                            onItemChange={this.destItemChange}>
                                            <GridToolbar>
                                                <div className="section-sub-header">
                                                    <h6>Extra Locations</h6>
                                                    <div className="toolbar-right">
                                                        <Button
                                                            title="Add new destination"
                                                            onClick={this.addDestination}
                                                            svgIcon={ plusIcon}
                                                            className="cmd add-item"
                                                            type="button"> Add destination</Button>
                                                    </div>
                                                </div>
                                            </GridToolbar>
                                            <GridColumn field="destinationType" width="140px" title="Destination type" resizable={true} editable={true}
                                                cells={{ data: this.destTypeCell }} />
                                            <GridColumn field="destination" width="120px" title="Destination" resizable={true} cells={{ data: (data) => this.textCells(data, this.onLocationItemChange) }} />
                                            <GridColumn field="folders" title="Folders ( ; separated )" resizable={true} cells={{ data: (data) => this.textCells(data, this.onLocationItemChange) }} />
                                            <GridColumn field="disabled" title="Disabled" width="80px" cells={{ data: (data) => this.booleanEdit(data, this.onLocationItemChange) }} />
                                            <GridColumn field="" title="" width="40px" cells={{ data: (data) => this.removeCell(data, this.removeDest) }} />
                                        </Grid>
                                    </div>
                                </div>
                            </div>
                            <div className="config-editor-data">
                                <div className="section-header">
                                    <h6>
                                        Data loader configuration
                                    </h6>
                                    <div className="data-switch">
                                        <Switch id="dataSwitch" checked={this.state.dataSwitch} onChange={this.toggleDataSwitch} className="data-status" />
                                    </div>
                                </div>
                                <div>
                                    <div className="section-body">
                                        <div className="row" style={{ display: this.state.dataSwitch ? "flex" : "none" }}>
                                            <div className="col-3">
                                                <div className="row">
                                                    <div className="col-6 info-label">
                                                        <label htmlFor="status">Status:</label>
                                                    </div>
                                                    <div className="col-6 data-input">
                                                        <Switch onLabel="Enabled" offLabel="Disabled" id="status" className="config-status"
                                                            checked={!this.state.configItem.dataConfig?.disabled} onChange={this.toggleDataConfigDisabled} />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 info-label">
                                                        <label htmlFor="archive">Archive folder:</label>
                                                    </div>
                                                    <div className="col-6 data-input">
                                                        <Input id="archive" name="dataConfig.archive"
                                                            value={this.state.configItem.dataConfig?.archive ?? ""}
                                                            required={true}
                                                            onChange={this.changeTextValue}
                                                            pattern="^\S*$" title="Archive folder" />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 info-label">
                                                        <label htmlFor="dbname">DB Name:</label>
                                                    </div>
                                                    <div className="col-6 data-input">
                                                        <DropDownList
                                                            id="dbname"
                                                            onChange={this.changeLookupItem}
                                                            name="dataConfig.dbName"
                                                            textField="name"
                                                            value={this.getDropdownValue(this.state.configItem.dataConfig?.dbName ??
                                                                "")}
                                                            data={this.getLookupDropdownValues("DBName")}
                                                            defaultItem=""
                                                            className="drop-down" />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 info-label">
                                                        <label htmlFor="colsep">Column sep.:</label>
                                                    </div>
                                                    <div className="col-2 data-input">
                                                        <Input id="colsep" name="dataConfig.colSep"
                                                            value={this.state.configItem.dataConfig?.colSep ?? ""}
                                                            required={true}
                                                            onChange={this.changeTextValue}
                                                            pattern="^\S*$" title="Column separator" />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 info-label">
                                                        <label htmlFor="truncateTable" title="Truncate table before insert/updae">Truncate table:</label>
                                                    </div>
                                                    <div className="col-1 data-input">
                                                        <Checkbox
                                                            id="truncateTable"
                                                            name="dataConfig.truncateTable"
                                                            value={this.state.configItem.dataConfig?.truncateTable ?? false}
                                                            onChange={this.changeBooleanValue}
                                                            label="" />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 info-label">
                                                        <label htmlFor="tablename">Table name</label>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 data-input">
                                                        <Input id="tablename" name="dataConfig.tableName"
                                                            value={this.state.configItem.dataConfig?.tableName ?? ""}
                                                            required={true}
                                                            onChange={this.changeTextValue}
                                                            className="full-input"
                                                            pattern="^\S*$" title="Table name" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-9 columns-grid-container">
                                                <Grid data={this.state.configItem.dataConfig?.columns?.filter(
                                                    c => !c.isRemoved)}
                                                    className="data-config-columns-grid"
                                                    resizable={true}
                                                    onItemChange={this.columnItemChange}
                                                //scrollable={"scrollable"}

                                                //style={{ minWidth: "calc(60vw)" }}
                                                >
                                                    <GridToolbar>
                                                        <div className="section-sub-header">
                                                            <h6>Columns</h6>
                                                            <div className="toolbar-right">
                                                                <Button
                                                                    title="Add new column"
                                                                    onClick={this.addColumn}
                                                                    svgIcon={ plusIcon}
                                                                    className="cmd add-item"
                                                                    type="button"> Add column</Button>
                                                            </div>
                                                        </div>
                                                    </GridToolbar>
                                                    <GridColumn field="" title="" width="40px" cells={{ data: (data) => this.removeCell(data, this.removeColumn) }} />
                                                    <GridColumn field="inColName" title="File column" width="280px" resizable cells={{ data: (data) => this.textCells(data, this.onDataColumnChange) }}  />
                                                    <GridColumn field="inColType" title="Type" width="75px" resizable  cells={{ data: this.dataTypeCell }} />
                                                    <GridColumn field="dbColName" title="DB Column" width="300px" resizable cells={{ data: (data) => this.textCells(data, this.onDataColumnChange) }}  />
                                                    <GridColumn field="maxWidth" editor="numeric" title="Max width" width="90px" resizable cells={{ data: (data) => this.numericEdit(data, this.onDataColumnChange) }}  />
                                                    <GridColumn field="nullable" title="Nullable" width="85px" resizable editor="boolean" cells={{ data: (data) => this.booleanEdit(data, this.onDataColumnChange) }} />
                                                    <GridColumn field="updatable" title="Updatable" width="100px" resizable editor="boolean" cells={{ data: (data) => this.booleanEdit(data, this.onDataColumnChange) }}  />
                                                    <GridColumn field="escapeNull" title="Esc NULL" width="80px" resizable editor="boolean" cells={{ data: (data) => this.booleanEdit(data, this.onDataColumnChange) }}  />
                                                    <GridColumn field="updateOnlyLatest" title="Upd only latest" width="140px" resizable editor="boolean" cells={{ data: (data) => this.booleanEdit(data, this.onDataColumnChange) }} />
                                                </Grid>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                }
                {this.state.confirmVisible &&
                    (
                        <Dialog title={this.state.confirmTitle} onClose={() => this.toggleConfirm()}>
                            <p
                                style={{
                                    margin: "25px",
                                    textAlign: "center",
                                }}>
                                Are you sure you want to continue?
                            </p>
                            <DialogActionsBar>
                                <button className="k-button" onClick={() => this.toggleConfirm()}>
                                    No
                                </button>
                                <button className="k-button" onClick={this.doConfirm}>
                                    Yes
                                </button>
                            </DialogActionsBar>
                        </Dialog>
                    )}
            </div>
        );
    }

    private sortGrid = (e: GridSortChangeEvent) => {
        const viewData = orderBy(this.state.viewData, e.sort);
        let selectedIndex = -1;
        let configItem = this.state.configItem;
        if (configItem) {
            selectedIndex =
                viewData.findIndex(d => d.partitionKey === configItem!.partitionKey && d.rowKey === configItem!.rowKey)
        }
        this.setState({
            selectedIndex,
            viewData,
            sort: e.sort
        });
    }

    private onFilterChange = (e: any) => {
        const viewData = filterBy(this.state.configData, e.filter);
        let selectedIndex = -1;
        let configItem = this.state.configItem;
        if (configItem) {
            selectedIndex =
                viewData.findIndex(d => d.partitionKey === configItem!.partitionKey && d.rowKey === configItem!.rowKey)
        }

        this.setState({
            selectedIndex,
            viewData,
            filter: e.filter
        });
    }

    private getConfigData = async (apiAccessToken: string) => {
        try {
            const resData = await Server.postJson(apiUrls.search, apiAccessToken, "");
            const configData: IConfigurationData[] = resData ? resData : [];
            configData.forEach(c => {
                if (c.dataConfig)
                    c.dataConfig.columnlist = c.dataConfig.columns.map(c => c.dbColName).join(",");
            });

            this.setState({
                configData,
                viewData: configData.slice()
            });
        } catch (e) {
            console.log("Exception getting configuration data: " + e);
        }
    }

    private getLookups = async (apiAccessToken: string) => {
        try {
            const resData = await Server.getJson(apiUrls.lookups, apiAccessToken);
            let lookups = resData ? resData : [];
            this.setState({
                lookups
            });
        } catch (e) {
            console.log("Exception getting lookup data: " + e);
        }
    }

    private booleanCell = (data: GridCellProps) => {
        if (!data || !data.dataItem) {
            return (<td />);
        }

        if (data.dataItem.inEdit) {
            return (<GridEditCell {...data} className="boolean-cell" editor="boolean" />);
        }

        return (
            <td className="disabled-cell boolean-cell">
                <Checkbox disabled={true} checked={this.getPropertyValue(data.dataItem, data.field)} />
            </td>
        );
    }

    private getPropertyValue(obj: any, property: string) {
        const props = property.split(".");
        let ds_prop: any = obj;
        for (let i = 0; i < props.length - 1 && ds_prop; i++) {
            ds_prop = ds_prop[props[i]];
        }
        return ds_prop ? ds_prop[props[props.length - 1]] : null;
    }

    private setPropertyValue(obj: any, property: string, newValue: any) {
        const props = property.split(".");
        let ds_prop: any = obj;
        for (let i = 0; i < props.length - 1; i++) {
            ds_prop = ds_prop[props[i]];
        }
        const oldValue = ds_prop[props[props.length - 1]];
        ds_prop[props[props.length - 1]] = newValue;
        return oldValue !== newValue;
    }

    private changeTextValue = (event: any) => {
        const configItem = this.state.configItem;
        if (this.setPropertyValue(configItem, event.target.name, event.target.value))
            this.setState({ configItem, edited: true });
    }

    private changeBooleanValue = (event: CheckboxChangeEvent) => {
        const configItem = this.state.configItem;
        if (this.setPropertyValue(configItem, event.target.element!.name, event.value))
            this.setState({ configItem, edited: true });
    }

    private initDataConfig(configItem: IConfigurationData) {
        if (!configItem.dataConfig!.columns)
            configItem.dataConfig!.columns = [];
        let id = 0;
        configItem.dataConfig!.columns.forEach(d => {
            d.inEdit = true;
            d.id = ++id;
        });
    }

    private initFileConfig(configItem: IConfigurationData) {
        if (!configItem.fileConfig.source)
            configItem.fileConfig.source = {} as any;
        if (!configItem.fileConfig.destinations)
            configItem.fileConfig.destinations = [];
        let id = 0;
        configItem.fileConfig.destinations.forEach(d => {
            d.inEdit = true;
            d.id = ++id;
        });
        configItem.fileConfig.source.removeFile = !!configItem.fileConfig.source.removeFile;
        configItem.fileConfig.source.regex = configItem.fileConfig.source.regex ?? "";
    }

    private cloneData(data: IConfigurationData): IConfigurationData {
        if (data.isNew)
            return data;
        const configItem = JSON.parse(JSON.stringify(data));
        if (!configItem.fileConfig.source)
            configItem.fileConfig.source = {};
        if (!configItem.fileConfig.destinations)
            configItem.fileConfig.destinations = [];

        if (configItem.dataConfig)
            this.initDataConfig(configItem);

        this.initFileConfig(configItem);

        return configItem;
    }

    private getByKey = (pKey: string, rKey: string): IConfigurationData | undefined =>
        this.state.configData.find(d => d.fileConfig.source.sourceKey === pKey && d.fileConfig.fileName === rKey);


    private selectRowWithUndo = (dataItem: any) => {
        const configItem = this.state.configItem;
        if (configItem && configItem.isNew) {
            this.removeItem(() => this.selectRow(dataItem, () => this.scrollToSelectedRow()));
        } else {
            this.selectRow(dataItem);
        }
    }

    private selectRow = (dataItem: any, callback?: any) => {

        const pKey = dataItem.partitionKey;
        const rKey = dataItem.rowKey;

        const configItem: IConfigurationData = dataItem.isNew ? dataItem : this.cloneData(this.getByKey(pKey, rKey)!);

        const selectedIndex = this.state.viewData.findIndex(d =>
            d.partitionKey === pKey &&
            d.rowKey === rKey);

        this.setState({
            configItem,
            selectedIndex,
            edited: false,
            dataSwitch: !!configItem.dataConfig
        }, callback);
    }

    private onRowClick = (event: any) => {
        if (event.dataItem.selected &&
            this.state.configItem &&
            event.dataItem.fileConfig.fileName === this.state.configItem.fileConfig.fileName &&
            event.dataItem.fileConfig.source.sourceKey === this.state.configItem.fileConfig.source.sourceKey) return;

        if (this.state.edited) {
            return this.toggleConfirm("Unsaved changes will be lost.", () => this.selectRowWithUndo(event.dataItem));
        }

        this.selectRow(event.dataItem);
    }

    private getLookupItems = (lookupName: string): ILookupItem[] => {
        return this.state.lookups[lookupName] || [];
    }

    private getLookupDropdownValues = (lookupName: string): IDropdownData[] => {
        const fileTypes = this.getLookupItems(lookupName);
        return fileTypes.map(item => { return { name: decodeURIComponent(item.name), value: decodeURIComponent(item.name) } as IDropdownData });
    }

    private parseLookupProperties = (properties: string): any => {
        if (!properties) {
            return {};
        }

        try {
            return JSON.parse(properties);
        } catch {
            return {};
        }
    }

    private changeLookupItem = (event: any) => {
        const configItem = this.state.configItem;
        if (this.setPropertyValue(configItem, event.target.props.name, event.target.value.value))
            this.setState({ configItem, edited: true });
    }

    private changeSourceKey = (event: any) => {
        const configItem = this.state.configItem;
        if (this.setPropertyValue(configItem, event.target.props.name, event.target.value.value)) {
            const key = event.target.value.value;
            const fileTypes = this.getLookupItems("SourceKey");
            const keyObj = fileTypes.find(item => item.name === key);
            const prop = this.parseLookupProperties(keyObj?.properties ?? "");
            configItem!.fileConfig.downloadType = prop.downloadType || "";
            this.setState({ configItem, edited: true });
        }
    }

    private getDropdownValue(value: string): IDropdownData {
        return { name: value, value };
    }

    private columnItemChange = (item: any) => {
        const configItem = this.state.configItem!;
        const data = configItem.dataConfig!.columns;
        const index = data.findIndex(d => d.id === item.dataItem.id);

        if (!data[index].inEdit) {
            return;
        }

        const oldValue = data[index][item.field];
        if (oldValue !== item.value) {
            data[index] = { ...data[index], [item.field]: item.value };

            this.setState({
                configItem,
                edited: true
            });
        }
    }

    private destItemChange = (item: any) => {
        const configItem = this.state.configItem!;
        const data = configItem.fileConfig.destinations;
        const index = data.findIndex(d => d.id === item.dataItem.id);

        if (!data[index].inEdit) {
            return;
        }

        const oldValue = data[index][item.field];
        if (oldValue !== item.value) {
            data[index] = { ...data[index], [item.field]: item.value };

            this.setState({
                configItem,
                edited: true
            });
        }
    }

    private dataTypeCell = (data: any) => {
        if (!data || !data.dataItem) {
            return (<td />);
        }
        const isdisabled: boolean = !(data.dataItem.inEdit);

        if (isdisabled) {
            return (
                <td className="disabled-cell">
                    {data.dataItem[data.field]}
                </td>
            );
        }
        const dropDownData = [
            { inColType: "A" },
            { inColType: "D" },
            { inColType: "F" },
            { inColType: "N" },
        ];

        return (
            <td>
                <DropDownCell {...data} dropDownData={dropDownData} textfield={data.field} valuefield={data.field
                } isDisabled={false} />
            </td>
        );
    }

    private destTypeCell = (data: any) => {
        if (!data || !data.dataItem) {
            return (<td />);
        }
        const isdisabled: boolean = !(data.dataItem.inEdit);

        if (isdisabled) {
            return (
                <td className="disabled-cell">
                    {data.dataItem[data.field]}
                </td>
            );
        }
        const destTypes = this.getLookupItems("DestinationType");
        const dropDownData = destTypes.map(li => {
            return {
                destinationType: li.name
            }
        });

        return (
            <td>
                <DropDownCell {...data} dropDownData={dropDownData} textfield={data.field} valuefield={data.field
                } isDisabled={false} />
            </td>
        );
    }

    private checkDeleteItem = () => {
        var configItem = this.state.configItem;
        if (!configItem) return;
        return this.toggleConfirm(`Item file ${configItem.fileConfig.fileName} will be deleted permanently.`, this.deleteItem);
    }

    private duplicateItem = () => {
        const newRecord: IConfigurationData = this.cloneData(this.state.configItem!);
        newRecord.partitionKey = "";
        newRecord.rowKey = "";
        newRecord.isNew = true;
        newRecord.disabled = true;
        newRecord.muleDisabled = true;
        const newvData = this.state.viewData;
        const selectedIndex = newvData.length;
        newvData.push(newRecord);
        this.setState({
            configItem: newRecord,
            viewData: newvData,
            selectedIndex,
            dataSwitch: true,
            edited: true
        } as IState, () => this.scrollToSelectedRow());
    }

    private removeItem = (callback?: any) => {
        const configItem = this.state.configItem!;
        const viewData = this.state.viewData;
        let rIndex =
            viewData.findIndex(d => d.partitionKey === configItem!.partitionKey && d.rowKey === configItem!.rowKey);
        if (rIndex !== -1) {
            viewData.splice(rIndex, 1);
        }
        const configData = this.state.configData;
        rIndex =
            configData.findIndex(d => d.partitionKey === configItem!.partitionKey && d.rowKey === configItem!.rowKey);
        if (rIndex !== -1) {
            configData.splice(rIndex, 1);
        }
        this.setState({
            configData,
            viewData,
            configItem: undefined,
            edited: false,
            loading: false,
            selectedIndex: -1
        }, callback);
    }

    private deleteItem = async () => {
        const configItem = this.state.configItem;
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        Server.deleteJson(
            `${apiUrls.crud}/${configItem!.fileConfig.source.sourceKey}/${configItem!.fileConfig.fileName}`,
            apiAccessToken,
            "")
            .then((resData: IConfigurationEntity) => {
                AppAlert.Success(
                    `Item with source key: ${resData.partitionKey} and file name: ${resData.rowKey} deleted.`);
                this.removeItem();
            })
            .catch((e) => {
                console.log("Exception getting configuration data: " + e);
                this.setState({
                    loading: false
                });
            });
    }

    private addDestination = () => {
        const configItem = this.state.configItem;
        const newItem: IFileDestination = {
            isNew: true,
            inEdit: true,
            id: configItem!.fileConfig.destinations.length + 1,
            destination: "",
            destinationType: "",
            folders: "",
            disabled: false
        };
        configItem!.fileConfig.destinations.push(newItem);
        this.setState({
            configItem,
            edited: true
        });
    }

    private addColumn = () => {
        const configItem = this.state.configItem;
        const newItem: IColumn = {
            isNew: true,
            inEdit: true,
            id: configItem!.dataConfig!.columns.length + 1,
            dbColName: "",
            inColName: "",
            inColType: "",
            maxWidth: 0,
            escapeNull: false,
            nullable: true,
            updatable: true
        };
        configItem!.dataConfig!.columns.push(newItem);
        this.setState({
            configItem,
            edited: true
        });
    }

    private removeDest = (data: any) => {
        const configItem = this.state.configItem;
        const dests = configItem!.fileConfig.destinations;
        const index = data.id - 1;
        dests[index].isRemoved = true;
        this.setState({
            configItem,
            edited: true
        });
    }

    private removeColumn = (data: any) => {
        const configItem = this.state.configItem;
        const columns = configItem!.dataConfig!.columns;
        const index = data.id - 1;
        columns[index].isRemoved = true;
        this.setState({
            configItem,
            edited: true
        });
    }

    private removeCell = (data: any, removeMethod: any) => {
        return (
            <td style={{ textAlign: "center" }} className="k-command-cell">
                <button className="k-button k-grid-remove-command" onClick={() => removeMethod(data.dataItem)} title="Remove item">
                    <SvgIcon icon={trashIcon} />
                </button>

            </td>);
    }

    private onDataColumnChange = (data: any, event: any) => {
        const value = event?.value ?? event.target?.element?.value ?? event.target!.value;
        const configItem = this.state.configItem;
        const index = data.id - 1;
        const records = configItem!.dataConfig!.columns;
        records[index][event.target.name] = value;
        this.setState({
            configItem,
            edited: true
        });
    }

    private booleanEdit = (data: any, onChangeMethod: any) => {
        return (
            <td style={{ textAlign: "center" }}>
                <Checkbox checked={this.getPropertyValue(data.dataItem, data.field)} name={`${data.field}`} onChange={(e) => onChangeMethod(data.dataItem, e)} />
            </td>);
    }

    private numericEdit = (data: any, onChangeMethod: any) => {
        return (
            <td style={{ textAlign: "center" }}>
                <NumericTextBox value={this.getPropertyValue(data.dataItem, data.field)} name={`${data.field}`} onBlur={(e) => onChangeMethod(data.dataItem, e)} />
            </td>);
    }

    private onLocationItemChange = (data: any, event: any) => {
        const value = event.value ?? event.target?.value;
        const configItem = this.state.configItem;
        const index = data.id - 1;
        const records = configItem!.fileConfig!.destinations;
        records[index][event.target.name] = value;
        this.setState({
            configItem,
            edited: true
        });
    }

    private textCells = (data: any, onChangeMethod: any) => {
        return (
            <td style={{ textAlign: "center" }}>
                <Input
                    name={`${data.field}` }
                    defaultValue={this.getPropertyValue(data.dataItem, data.field)}
                    onBlur={(e) => onChangeMethod(data.dataItem, e)}
                    />
            </td>);
    }

    

    private exportConfig = async () => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);

        Server.postJson(apiUrls.export, apiAccessToken, "{}", true)
            .then((res) => {
                var data = new Blob([res], { type: 'text/csv' });
                var csvURL = window.URL.createObjectURL(data);
                var tempLink = document.createElement('a');
                tempLink.href = csvURL;
                tempLink.setAttribute('download', 'GDLConfigExport_' + moment().format("YYYYMMDDhhmmss") + '.csv');
                tempLink.click();
            })
            .catch((e) => {
                console.log("Exception exporting config: " + e);
            });
    }

    private checkAddItem = () => {
        if (this.state.edited) {
            return this.toggleConfirm("Unsaved changes will be lost.", () => this.addItem());
        }

        this.addItem();
    }

    private addItem = () => {
        const newRecord = {
            isNew: true,
            partitionKey: "",
            rowKey: "",
            disabled: false,
            muleDisabled: true,
            dataConfig: {
                disabled: false,
                columns: [],
                archive: "",
                colSep: ",",
                dbName: "",
                tableName: "",
                truncateTable: false,
                updateOnlyLatest: false,
                columnlist: ""
            },
            fileConfig: {
                fileName: "",
                fileType: ".csv",
                downloadType: "",
                frequency: "5",
                destinations: [],
                source: { sourceKey: "", sourceName: "", sourceFolder: "", sourceArchive: "", removeFile: false, regex: "" }
            }
        } as IConfigurationData;
        newRecord.isNew = true;
        const newvData = this.state.viewData;
        newvData.splice(0, 0, newRecord);
        const selectedIndex = 0;
        this.setState({
            configItem: newRecord,
            viewData: newvData,
            selectedIndex,
            dataSwitch: true,
            edited: true
        } as IState, () => this.scrollToSelectedRow());

    }

    private listErrors = () => {
        if (!this.state.edited || !this.state.validationData.errors || !this.state.validationData.errors.length)
            return (<span />);
        return (
            <ul className="errors" title="Errors">
                {this.state.validationData.errors.map((err, key) => <li key={key}>{err}</li>)}
            </ul>
        );
    }

    private listWarnings = () => {
        if (!this.state.edited || !this.state.validationData.warnings || !this.state.validationData.warnings.length)
            return (<span />);
        return (
            <ul className="warnings" title="Warnings">
                {this.state.validationData.warnings.map((err, key) => <li key={key}>{err}</li>)}
            </ul>
        );
    }

    private validateConfigItem = (): IValidationData => {
        const addToArray = (arr: string[], value: string) => {
            if (arr.indexOf(value) === -1)
                arr.push(value);
        };

        const errors = [] as string[];
        const warnings = [] as string[];
        const configItem = this.state.configItem!;

        if (!configItem.fileConfig.fileName)
            errors.push("File name is required");
        if (!configItem.fileConfig.fileType)
            errors.push("File type is required");
        if (!configItem.fileConfig.source.sourceKey)
            errors.push("Source key is required");
        if (!configItem.fileConfig.source.sourceName)
            errors.push("Source name is required");
        if (!configItem.fileConfig.frequency)
            errors.push("Frequency is required");
        configItem.fileConfig.destinations.forEach(d => {
            if (!d.destinationType)
                addToArray(errors, "Extra Locations: Destination type must not be empty");
            //if (!d.destination)
            //    addToArray(errors, "Extra Locations: Destination must not be empty");
            if (!d.folders)
                addToArray(errors, "Extra Locations: Folders must not be empty");
        });
        if (this.state.dataSwitch && configItem.dataConfig) {
            if (!configItem.dataConfig.archive)
                errors.push("Archive folder is required");
            if (!configItem.dataConfig.dbName)
                errors.push("DB Name is required");
            if (!configItem.dataConfig.colSep)
                errors.push("Column separator is required");
            if (!configItem.dataConfig.tableName)
                errors.push("Table name is required");
            else {
                const dup = this.state.configData.find(d =>
                    d.dataConfig?.tableName === configItem.dataConfig!.tableName &&
                    (d.partitionKey !== configItem.partitionKey || d.rowKey !== configItem.rowKey));
                if (dup)
                    warnings.push(`Table name is already used in file: ${dup.rowKey}`);
            }
            if (!configItem.dataConfig.columns.length)
                errors.push("Columns cannot be empty");
            configItem.dataConfig.columns.filter(c => !c.isRemoved).forEach(c => {
                if (!c.inColName)
                    addToArray(errors, "Columns: File column name must not be empty");
                if (!c.inColType)
                    addToArray(errors, "Columns: Column type must not be empty");
                if (!c.dbColName)
                    addToArray(errors, "Columns: DB column name must not be empty");
                if (c.maxWidth < 0)
                    addToArray(errors, "Columns: Max width should not be negative");
            });
        }


        return { errors, warnings };
    }

    private checkApplyChanges = () => {
        const validationData = this.validateConfigItem();
        if (validationData.errors.length || validationData.warnings.length) {
            this.setState({ validationData });
            if (validationData.errors.length)
                return;
            return this.toggleConfirm("There are some warnings.", this.applyChanges);
        }

        this.applyChanges();
    }

    private applyChanges = async () => {
        const configItem = this.state.configItem;
        if (!configItem)
            return;

        if (configItem.dataConfig?.columns) {
            configItem.dataConfig.columns = configItem.dataConfig.columns.filter(c => !c.isRemoved);
        }

        configItem.fileConfig.destinations = configItem.fileConfig.destinations.filter(c => !c.isRemoved);


        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });

        Server.postJson(`${apiUrls.crud}/${configItem!.fileConfig.source.sourceKey}/${configItem!.fileConfig.fileName}`,
            apiAccessToken,
            JSON.stringify(configItem))
            .then((resData: IConfigurationEntity) => {
                AppAlert.Success(
                    `Item with source key: ${resData.partitionKey} and file name: ${resData.rowKey} inserted/updated.`);
                configItem!.partitionKey = resData.partitionKey;
                configItem!.rowKey = resData.rowKey;
                configItem!.user = resData.user;
                const isNew = configItem!.isNew;
                if (isNew) {
                    configItem!.isNew = false;
                }
                const viewData = this.state.viewData;
                const configData = this.state.configData;
                if (this.state.selectedIndex !== -1)
                    viewData[this.state.selectedIndex!] = configItem!;

                if (isNew) {
                    configData.splice(0, 0, configItem!);
                }
                else {
                    const dataIndex = configData.findIndex(item =>
                        item.fileConfig.source.sourceKey === configItem!.fileConfig.source.sourceKey &&
                        item.fileConfig.fileName === configItem!.fileConfig.fileName);
                    configData[dataIndex] = configItem!;
                }
                this.setState({
                    configData,
                    viewData,
                    configItem,
                    edited: false,
                    validationData: {} as IValidationData,
                    loading: false,
                });


            })
            .catch((e) => {
                console.log("Exception getting configuration data: " + e);
                this.setState({
                    loading: false
                });
            });
    }

    private checkCancelChanges = () => {
        return this.toggleConfirm("All changes will be cancelled.", this.cancelChanges);
    }

    private cancelChanges = () => {
        if (this.state.configItem!.isNew) {
            this.removeItem();
        } else {
            const configItem: IConfigurationData = this.cloneData(this.state.viewData[this.state.selectedIndex!]);
            this.setState({
                configItem,
                edited: false,
                dataSwitch: !!configItem.dataConfig
            });
        }
    }

    private toggleConfirm = (confirmTitle: string = "", confirmAction: any = null) => {
        this.setState({
            confirmAction,
            confirmTitle,
            confirmVisible: !this.state.confirmVisible
        });
    }

    private doConfirm = () => {
        if (this.state.confirmAction) {
            this.state.confirmAction();
            this.setState({
                confirmVisible: false
            });
        }
    }

    private toggleDataSwitch = (event: any) => {
        const configItem = this.state.configItem;
        const dataSwitch = event.target.value;
        if (dataSwitch) {
            if (!configItem!.dataConfig) {
                configItem!.dataConfig = {} as IDataLoaderConfig;
                this.initDataConfig(configItem!);
            }
        }
        else {
            configItem!.dataConfig = undefined;
        }
        this.setState({
            dataSwitch,
            edited: true,
            configItem
        });
    }

    private toggleDisabled = (event: any) => {
        const configItem = this.state.configItem;
        if (event.target.name !== "gdlStatus") {
            configItem!.muleDisabled = !event.target.value;
            if (configItem!.muleDisabled === false && configItem!.disabled === false) {
                configItem!.disabled = true;
            }
        }
        else {
            configItem!.disabled = !event.target.value;
            if (configItem!.disabled === false && configItem!.muleDisabled === false) {
                configItem!.muleDisabled = true;
            }
        }
        
        this.setState({
            edited: true,
            configItem
        });
    }

    private toggleDataConfigDisabled = (event: any) => {
        const configItem = this.state.configItem;
        configItem!.dataConfig!.disabled = !event.target.value;
        this.setState({
            edited: true,
            configItem
        });
    }

    private dateCell = () => {
        return class extends React.Component<GridCellProps> {
            public render() {
                const dataItem = this.props.dataItem;
                const value = this.props.field ? moment(dataItem[this.props.field]).format("yyyy-MM-DD") : '';
                const hint = this.props.field ? moment(dataItem[this.props.field]).format("yyyy-MM-DD HH:mm:ss") : '';
                return (
                    <td className={this.props.className} style={this.props.style} title={hint}>
                        {value}
                    </td>
                );
            }
        }
    }

    private columnsCell = () => {
        const getPropertyValue = (obj, prop) => this.getPropertyValue(obj, prop);
        return class extends React.Component<GridCellProps> {
            public render() {
                const dataItem = this.props.dataItem;
                const value = getPropertyValue(dataItem, this.props.field!);
                return (
                    <td className={this.props.className} style={this.props.style} title={value}>
                        {value}
                    </td>
                );
            }
        }
    }

    private scrollToSelectedRow = () => {
        if (this.grid) {
            this.grid.scrollIntoView({ rowIndex: this.state.selectedIndex });
        }
    }
    private pageChange = (e: any) => {
        this.setState({
            skip: e.page.skip
        } as IState);
    }

    
}

